import React, { useCallback, useMemo } from "react";

import { ProductData } from "data/product/products";

import { ReviewModal } from "~/components/BTSInProductVideoPage/Review/Modals";
import { ReviewForm } from "~/components/BTSInProductVideoPage/Review/Modals/reviewTypes";

interface ReviewState {
  productInfo: ProductData;
  setProductInfo({}: ProductData): void;
  onCloseModal(): void;
  openReviewModal(): void;
  isReviewModalOpen: boolean;
  updateActiveReviewModal(): void;
  lastClassVideo: boolean;
  setLastClassVideo(value: boolean): void;
}

export const ReviewModalContext = React.createContext<ReviewState>({
  productInfo: {} as ProductData,
  setProductInfo: () => {},
  onCloseModal: () => {},
  openReviewModal: () => {},
  updateActiveReviewModal: () => {},
  lastClassVideo: false,
  isReviewModalOpen: false,
  setLastClassVideo: () => {},
});

interface ReviewModalProviderProps {
  children: React.ReactNode;
}

export const ReviewModalProvider = ({ children }: ReviewModalProviderProps) => {
  const [productInfo, setProductInfo] = React.useState({} as ProductData);
  const [lastClassVideo, setLastClassVideo] = React.useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = React.useState(false);
  const [activeReviewModal, setActiveReviewModal] = React.useState<ReviewForm>(ReviewForm.Rate);

  const openReviewModal = useCallback((activeReviewModal?: ReviewForm) => {
    setIsReviewModalOpen(true);
    if (activeReviewModal) {
      setActiveReviewModal(activeReviewModal);
    }
  }, []);

  const onCloseModal = useCallback(() => {
    setActiveReviewModal(ReviewForm.Rate);
    setIsReviewModalOpen(false);
  }, []);

  const handlePrevious = useCallback(() => {
    if (activeReviewModal === ReviewForm.Review) {
      setActiveReviewModal(ReviewForm.Rate);
    }

    if (activeReviewModal === ReviewForm.Success) {
      setActiveReviewModal(ReviewForm.Review);
    }
  }, [activeReviewModal]);

  const updateActiveReviewModal = useCallback(() => {
    setIsReviewModalOpen(true);
    setActiveReviewModal(ReviewForm.Review);
  }, [setActiveReviewModal]);

  const contextValue = useMemo(
    () => ({
      lastClassVideo,
      productInfo,
      onCloseModal,
      setProductInfo,
      openReviewModal,
      isReviewModalOpen,
      setLastClassVideo,
      updateActiveReviewModal,
    }),
    [
      productInfo,
      onCloseModal,
      setProductInfo,
      lastClassVideo,
      openReviewModal,
      isReviewModalOpen,
      setLastClassVideo,
      updateActiveReviewModal,
    ],
  );

  return (
    <ReviewModalContext.Provider value={contextValue}>
      {children}
      <ReviewModal
        onClose={onCloseModal}
        isOpen={isReviewModalOpen}
        handlePrevious={handlePrevious}
        activeReviewModal={activeReviewModal}
        setActiveModal={setActiveReviewModal}
      />
    </ReviewModalContext.Provider>
  );
};
